import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import injectSheet from 'react-jss';
import { connect } from 'react-redux';

import { showToast } from '~/components/Shared/EcToast/showToast';
import { Button } from '~/eds/src/components/Button/Button';
import { getUserName } from '~/eds/src/utils/getUserName';
import { EntityType } from '~/enums/EntityType';
import { TicketStatusType } from '~/enums/TicketStatusType';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { useClientId } from '~/hooks/useClientId';
import { api } from '~/redux/api/createApi';
import { ERROR, SUCCESS } from '~/types/toast.types';
import FlexLayout from '~/ui/components/FlexLayout/FlexLayout';
import Text from '~/ui/components/Text/Text';
import { useModal, useModalSimple } from '~/ui/hooks/modal';
import { validateEmail } from '~/utils/strings';

import SortRecipients from './SortRecipients';
import TicketRecipientsList from './TicketRecipientsList';
import styles from './TicketSigningPhase.styles';
import UserEmailSearch from './UserEmailSearch';

const TicketSigningPhase = ({
  phaseId,
  // injected props
  classes,
  // connected props
  phases,
  judgments,
  risks,
  approvals,
  hasEnvelope,
  ticketId,
  stages,
}) => {
  const hasCCRecipientsFlag = useFlag(FlagType.CCRecipients);
  const isEsignatureV2RevampEnabled = useFlag(FlagType.EsignatureV2Revamp);
  const clientId = useClientId();
  const intl = useIntl();

  const phase = phases[phaseId];
  const { judgmentIds, riskIds } = phase;

  const originalRecipients = useMemo(() => {
    return [
      ...judgmentIds.map((judgmentId) => {
        const judgment = judgments[judgmentId];
        return {
          ...judgment,
          entityType: EntityType.TicketJudgment,
        };
      }),
      ...riskIds.map((riskId) => ({
        ...risks[riskId],
        entityType: EntityType.Risk,
        newCC: false,
      })),
    ].filter(removeCCSigners);
  }, [judgmentIds]);

  const [updateRecipients] = api.endpoints.updateRecipients.useMutation();
  const [deleteRecipients] = api.endpoints.deleteRecipients.useMutation();
  const [resolveUser] = api.endpoints.resolveUser.useLazyQuery();

  const [editMode, setEditMode] = useState(false);

  const [newRecipient, setNewRecipient] = useState(null);
  const [recipients, setRecipients] = useState(originalRecipients);

  // once the ticket envelope is created signers order can't be modified
  // or if any user already signed the ticket uploading a document
  const isEditActive =
    !hasEnvelope &&
    stages.sign?.status !== TicketStatusType.Signed &&
    !isEsignatureV2RevampEnabled;

  const [sortRecipientsModal, showSortRecipientsModal] =
    useSortRecipientsModal();

  function removeCCSigners(judgment) {
    const judgmentApproval =
      judgment.approvalIds && approvals[judgment.approvalIds[0]];
    return (
      judgmentApproval &&
      (hasCCRecipientsFlag ||
        judgmentApproval.status !== TicketStatusType.NotApplicable)
    );
  }

  function showEdit() {
    if (hasCCRecipientsFlag) {
      setEditMode(true);
    } else {
      showSortRecipientsModal();
    }
  }

  useEffect(() => {
    setRecipients([...originalRecipients]);
  }, [originalRecipients]);

  function hasInvalidRecipient(recipient) {
    if (recipient) {
      if (recipient.userId) return false;

      if (recipient.name.length) {
        return !validateEmail(recipient.email);
      }
      return true;
    }
    return true;
  }

  const internalRecipients = useMemo(() => {
    const internal = recipients.filter(
      (recipient) =>
        (recipient.newCC && recipient.userId) ||
        (recipient.approvalIds &&
          recipient.approvalIds.length &&
          approvals[recipient.approvalIds[0]].userId),
    );
    return internal.map((recipient) => {
      if (recipient.newCC) {
        return recipient.userId;
      } else {
        const judgment = approvals[recipient.approvalIds[0]];
        return judgment.userId;
      }
    });
  }, [recipients]);

  const [addCCModal, showAddCCModal] = useModal({
    actionButton: {
      disabled: hasInvalidRecipient(newRecipient),
      text: intl.formatMessage({
        id: 'TicketSigningPhase.saveButton',
        defaultMessage: 'Save',
        description: 'Save button text',
      }),
      errorHandler: () =>
        showToast(
          ERROR,
          intl.formatMessage({
            id: 'TicketSigningPhase.addCCRecipientError',
            defaultMessage:
              'An error ocurred adding the Carbon Copy Recipient.',
            description: 'Error message when adding CC recipient fails',
          }),
        ),
      promise: async () => {
        setRecipients([
          ...recipients,
          {
            name: newRecipient.name,
            email: newRecipient.email,
            userId: newRecipient.userId,
            newCC: true,
            deleteCC: false,
            position: recipients.length,
          },
        ]);
      },
    },
    text: intl.formatMessage({
      id: 'TicketSigningPhase.saveModalText',
      defaultMessage: 'Save',
      description: 'Save modal text',
    }),
    title: intl.formatMessage({
      id: 'TicketSigningPhase.addCCRecipientTitle',
      defaultMessage: 'Add CC Recipient',
      description: 'Modal title for adding CC recipient',
    }),
    overflowDisplay: 'visible',
    content: (
      <UserEmailSearch
        internalRecipientIds={internalRecipients}
        setRecipient={setNewRecipient}
      />
    ),
  });

  function useSortRecipientsModal() {
    const [modal, showModal] = useModalSimple({
      confirm: onSave,
      text: intl.formatMessage({
        id: 'TicketSigningPhase.sortButton',
        defaultMessage: 'Sort',
        description: 'Sort button text',
      }),
      title: intl.formatMessage({
        id: 'TicketSigningPhase.rearrangeSigningOrderTitle',
        defaultMessage: 'Re-arrange the signing order.',
        description: 'Modal title for rearranging signing order',
      }),
      content: (
        <SortRecipients
          recipients={recipients}
          approvals={approvals}
          judgments={judgments}
          stage={phase.stage}
          onSort={setRecipients}
        />
      ),
    });
    return [modal, showModal];
  }

  async function onDelete(index) {
    const recipient = recipients[index];
    if (recipient.newCC) {
      setRecipients([
        ...recipients.slice(0, index),
        ...recipients.slice(index + 1),
      ]);
    } else {
      try {
        const judgmentResult = approvals[recipient.approvalIds[0]];
        let user = judgmentResult.externalUser;
        if (!user) {
          user = await resolveUser({
            id: judgmentResult.userId,
            params: {
              clientId,
            },
          }).unwrap();
        }

        await deleteRecipients({
          ticketId,
          recipients: [
            {
              email: user.email,
              judgmentResultId: judgmentResult.id,
              ...(judgmentResult.externalUser
                ? { externalUserId: user.id, name: user.name }
                : {
                    userId: user.id,
                    name: getUserName({
                      user,
                      intl,
                    }),
                  }),
            },
          ],
        }).unwrap();
        showToast(
          SUCCESS,
          intl.formatMessage({
            id: 'TicketSigningPhase.recipientDeletedSuccess',
            defaultMessage: 'Recipient was suscessfully deleted.',
            description: 'Success message when recipient is deleted',
          }),
        );
      } catch {
        showToast(
          ERROR,
          intl.formatMessage({
            id: 'TicketSigningPhase.recipientDeleteError',
            defaultMessage: 'An error occured when deleting the recipient.',
            description: 'Error message when deleting recipient fails',
          }),
        );
      }
    }
  }

  async function onSave() {
    try {
      await updateRecipients({
        ticketId,
        updatedRecipients: recipients.map((recipient, index) => ({
          newCC: !!recipient.newCC,
          judgmentResultId:
            recipient.approvalIds && recipient.approvalIds.length
              ? recipient.approvalIds[0]
              : null,
          position: index,
          ...(recipient.newCC
            ? {
                userId: recipient.userId,
                email: recipient.email,
                name: recipient.name,
              }
            : {}),
        })),
      }).unwrap();
      showToast(
        SUCCESS,
        intl.formatMessage({
          id: 'TicketSigningPhase.recipientsEditedSuccess',
          defaultMessage: 'Recipients edited with success.',
          description:
            'Success message when recipients are edited successfully',
        }),
      );
      setEditMode(false);
    } catch {
      showToast(
        ERROR,
        intl.formatMessage({
          id: 'TicketSigningPhase.recipientsEditError',
          defaultMessage: 'An error occurred when editing the recipients.',
          description: 'Error message when editing recipients fails',
        }),
      );
    }
  }
  if (editMode) {
    return (
      <>
        <FlexLayout flexDirection="column" space={4}>
          <Text color="gray-600" variant="xs-dense-caps">
            <FormattedMessage
              id="TicketSigningPhase.recipientsLabel"
              defaultMessage="Recipients"
              description="Label for recipients section"
            />
          </Text>
          <Text variant="xs-dense">
            <FormattedMessage
              id="TicketSigningPhase.rearrangeSigningOrderText"
              defaultMessage="Re-arrange the signing order"
              description="Text explaining users can rearrange signing order"
            />
          </Text>
          <span>
            <SortRecipients
              recipients={recipients}
              approvals={approvals}
              judgments={judgments}
              stage={phase.stage}
              onSort={setRecipients}
              onDelete={onDelete}
            />
          </span>
          <FlexLayout
            alignItems="center"
            justifyContent="flex-end"
            px={4}
            space={4}
          >
            <Button
              preset="cancel"
              text={intl.formatMessage({
                id: 'TicketSigningPhase.cancelButton',
                defaultMessage: 'Cancel',
                description: 'Cancel button text',
              })}
              onClick={() => {
                setRecipients([...originalRecipients]);
                setEditMode(false);
              }}
            />
            <Button
              variant="primary"
              onClick={showAddCCModal}
              text={intl.formatMessage({
                id: 'TicketSigningPhase.addCCButton',
                defaultMessage: 'Add CC',
                description: 'Add CC button text',
              })}
            />
            <Button
              variant="primary"
              onClick={onSave}
              text={intl.formatMessage({
                id: 'TicketSigningPhase.saveChangesButton',
                defaultMessage: 'Save',
                description: 'Save changes button text',
              })}
            />
          </FlexLayout>
        </FlexLayout>
        {addCCModal}
      </>
    );
  }

  return (
    <>
      <FlexLayout flexDirection="column" space={3}>
        <FlexLayout justifyContent="space-between">
          <Text color="gray-600" variant="xs-dense-caps">
            <FormattedMessage
              id="TicketSigningPhase.recipientsHeader"
              defaultMessage="Recipients"
              description="Recipients section header"
            />
          </Text>
          {isEditActive && (
            <Button
              text={intl.formatMessage({
                id: 'TicketSigningPhase.editButton',
                defaultMessage: 'Edit',
                description: 'Edit button text',
              })}
              icon="edit"
              iconPosition="left"
              variant="action"
              onClick={showEdit}
            />
          )}
        </FlexLayout>
        <FlexLayout flexDirection="column" space={3}>
          <TicketRecipientsList
            recipients={originalRecipients}
            approvals={approvals}
          />
        </FlexLayout>
      </FlexLayout>
      {sortRecipientsModal}
    </>
  );
};

TicketSigningPhase.propTypes = {
  phases: PropTypes.object.isRequired,
  phaseId: PropTypes.string.isRequired,
};

function mapStateToProps({ ticket }) {
  const { phases, judgments, risks, approvals, hasEnvelope, id, stages } =
    ticket;
  return {
    phases,
    judgments,
    risks,
    approvals,
    hasEnvelope,
    ticketId: id,
    stages,
  };
}

export default injectSheet(styles)(
  connect(mapStateToProps)(TicketSigningPhase),
);
